import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Zap, Trophy, Users, ArrowRight, MessageSquare, Shield, Clock, DollarSign, Star, Quote, BookOpen, CheckCircle, Target, Mic, Briefcase, HelpCircle, Building2, UserCog } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import Header from "@/components/layout/Header";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import Footer from "@/components/layout/Footer";
import HeroObjecaoChat from "@/components/HeroObjecaoChat";
import juniorBianchimAsset from "@/assets/junior-bianchim-novo.jpg.asset.json";
import juniorBianchimNoBg from "@/assets/junior-busto-nobg.png";
const juniorBianchim = juniorBianchimAsset.url;
import postCoverTecnicas from "@/assets/cover-tecnicas-fechamento.jpg";
import postCoverIA from "@/assets/cover-ia-treinamento.jpg";
import postCoverObjecoes from "@/assets/cover-objecoes-preco.jpg";
import postCoverGamificacao from "@/assets/cover-gamificacao.jpg";
import postCoverColdcalling from "@/assets/cover-cold-calling.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";
import postCoverComoVenderMais from "@/assets/cover-como-vender-mais.jpg";
import postCoverPreVendas from "@/assets/cover-pre-vendas.jpg";
import postCoverSocialSelling from "@/assets/cover-social-selling.jpg";
import postCoverNegociacao from "@/assets/cover-negociacao-b2b.jpg";
import postCoverFunil from "@/assets/cover-funil-vendas.jpg";
import postCoverCopa from "@/assets/cover-licoes-copa-2026.jpg";
import postCoverMontarEquipe from "@/assets/cover-montar-equipe-vendas.jpg";
import postCoverScript from "@/assets/cover-script-de-vendas.jpg";
import postCoverTreinarNovos from "@/assets/cover-treinar-vendedores-novos.jpg";
import postCoverKpis from "@/assets/cover-metricas-kpis-vendas.jpg";
import postCoverProspeccao from "@/assets/cover-prospeccao-b2b.jpg";
import postCoverPlaybook from "@/assets/cover-playbook-de-vendas.jpg";
import postCoverPipeline from "@/assets/cover-gestao-de-pipeline.jpg";
import postCoverRapport from "@/assets/cover-rapport-persuasao.jpg";
import postCoverConsultor from "@/assets/cover-o-que-faz-consultor-comercial.jpg";
import postCoverConsultoriaVendas from "@/assets/cover-consultoria-de-vendas.jpg";
import postCoverFollowUp from "@/assets/cover-follow-up-que-converte.jpg";
const Landing = () => {
  const navigate = useNavigate();
  const {
    user,
    loading
  } = useAuth();
  const [pricingPeriod, setPricingPeriod] = useState<'monthly' | 'annual'>('annual');
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);
  const objections = [{
    icon: DollarSign,
    objection: "É muito caro investir em treinamento de vendas",
    response: "O custo de um vendedor mal treinado é muito maior. Cada venda perdida por despreparo custa mais do que meses de assinatura. Com o Vendas Trainer, você tem ROI mensurável desde o primeiro mês."
  }, {
    icon: Clock,
    objection: "Não tenho tempo para treinar minha equipe",
    response: "Nosso sistema de IA treina sua equipe 24/7, sem precisar de um coordenador presente. Os vendedores treinam no próprio ritmo, em simulações rápidas de 5-10 minutos."
  }, {
    icon: MessageSquare,
    objection: "Minha equipe já sabe vender, não precisa de treinamento",
    response: "O mercado muda constantemente. Até os melhores vendedores precisam se atualizar. Nossa plataforma identifica gaps de performance e trabalha exatamente onde cada vendedor precisa melhorar."
  }, {
    icon: Shield,
    objection: "IA não consegue simular uma negociação real",
    response: "Nossa IA foi treinada com milhares de cenários reais de vendas. Ela adapta respostas baseadas no contexto, objeções e até no humor do cliente virtual. É o mais próximo do real sem arriscar vendas de verdade."
  }];
  const testimonials = [{
    name: "Ciene Rodrigues",
    role: "CEO",
    company: "Zentai Benefícios",
    avatar: "CR",
    rating: 5,
    text: "Treinamento excelente com foco em IA aplicada às vendas. Ampliou nossa visão do processo comercial com insights práticos que elevaram a performance da equipe. Recomendo para quem quer desenvolver sua equipe e aumentar resultados."
  }, {
    name: "Silvete Araujo",
    role: "Diretora de Negócios",
    company: "Zentai Benefícios",
    avatar: "SA",
    rating: 5,
    text: "Uma experiência extremamente enriquecedora, que trouxe novas perspectivas sobre o uso da inteligência artificial em negociações e estratégias comerciais. Um treinamento de alto nível, que transforma a forma como conduzimos negócios e fortalecemos relacionamentos."
  }, {
    name: "Giovanna Amaro Gireli",
    role: "Coordenadora de Marketing e Desenvolvimento Técnico",
    company: "Citróleo Group",
    avatar: "GG",
    rating: 5,
    text: "O treinamento com o Junior foi uma experiência extremamente valiosa. De forma dinâmica, ele trouxe insights poderosos para o time e também no nível pessoal, contribuindo diretamente para nossa evolução profissional e para o desenvolvimento da carreira."
  }, {
    name: "Marivaldo Souza",
    role: "CEO",
    company: "Triplos Tecnologia",
    avatar: "MS",
    rating: 5,
    text: "O treinamento proporcionou diversos insights e aprendizados valiosos. Compreendemos que todos têm um papel importante nas vendas. Tenho plena confiança de que nossa equipe vai evoluir e aproveitar cada oportunidade com mais estratégia e confiança."
  }];
  const blogPosts = [{
    title: "Follow-up Que Converte: Como Fazer Follow-up em Vendas e Quando Insistir",
    excerpt: "Cadência de follow-up, canais certos, mensagens prontas e o ponto exato entre insistir no cliente e encerrar com elegância.",
    category: "Técnicas de Vendas",
    date: "10 Set 2026",
    readTime: "11 min",
    image: postCoverFollowUp,
    link: "/posts/follow-up-que-converte"
  }, {
    title: "O Que Faz um Consultor Comercial (e Quando Sua Empresa Precisa de Um)",
    excerpt: "Atribuições do dia a dia, diferença entre consultor comercial e vendedor, e os sinais de que sua operação precisa de um consultor de vendas.",
    category: "Gestão Comercial",
    date: "09 Set 2026",
    readTime: "10 min",
    image: postCoverConsultor,
    link: "/posts/o-que-faz-um-consultor-comercial"
  }, {
    title: "Consultoria de Vendas: O Que É, Quanto Custa e Que Resultado Esperar",
    excerpt: "Como funciona um projeto de consultoria de vendas etapa por etapa, formatos de investimento, prazos de resultado e como escolher quem contratar.",
    category: "Estruturação Comercial",
    date: "08 Set 2026",
    readTime: "11 min",
    image: postCoverConsultoriaVendas,
    link: "/posts/consultoria-de-vendas"
  }, {
    title: "Rapport e Persuasão: Técnicas de Rapport e Gatilhos Mentais Para Vender Mais",
    excerpt: "Técnicas de rapport, como gerar confiança na venda e gatilhos mentais éticos para conduzir negociações com mais conexão e conversão.",
    category: "Técnicas de Vendas",
    date: "06 Set 2026",
    readTime: "11 min",
    image: postCoverRapport,
    link: "/posts/rapport-persuasao"
  }, {
    title: "Gestão de Pipeline: Como Gerenciar o Funil de Vendas e Prever Receita",
    excerpt: "Etapas do funil por decisão do cliente, higiene do CRM para vendas, cobertura de pipeline, previsão comprometida e a revisão semanal que move negócio.",
    category: "Gestão Comercial",
    date: "05 Set 2026",
    readTime: "12 min",
    image: postCoverPipeline,
    link: "/posts/gestao-de-pipeline"
  }, {
    title: "Playbook de Vendas: Como Criar o Manual do Vendedor Que o Time Usa",
    excerpt: "Os 8 capítulos do manual do vendedor, como escrever a partir do que já funciona e como implantar o playbook comercial sem virar burocracia.",
    category: "Processo Comercial",
    date: "04 Set 2026",
    readTime: "12 min",
    image: postCoverPlaybook,
    link: "/posts/playbook-de-vendas"
  }, {
    title: "Prospecção B2B: Como Prospectar Clientes e Encher o Pipeline",
    excerpt: "ICP e gatilhos, cadência de prospecção outbound em 15 dias, canais que mais geram reunião e as métricas de geração de leads B2B.",
    category: "Prospecção",
    date: "02 Set 2026",
    readTime: "13 min",
    image: postCoverProspeccao,
    link: "/posts/prospeccao-b2b"
  }, {
    title: "Métricas e KPIs de Vendas: Os Benchmarks Para Saber se Seus Números Estão Bons",
    excerpt: "KPI vendas e métricas comerciais com exemplos numéricos e benchmarks por setor: conversão, ciclo, win rate, CAC, LTV e desempenho do vendedor.",
    category: "Gestão Comercial",
    date: "31 Ago 2026",
    readTime: "14 min",
    image: postCoverKpis,
    link: "/posts/metricas-e-kpis-de-vendas"
  }, {
    title: "Como Treinar Vendedores Novos: O Que Realmente Encurta o Ramp-up",
    excerpt: "Onboarding vendedor sem manual engessado: treinamento da equipe comercial na prática e o que de fato encurta o ramp-up de vendas.",
    category: "Desenvolvimento de Time",
    date: "28 Ago 2026",
    readTime: "11 min",
    image: postCoverTreinarNovos,
    link: "/posts/como-treinar-vendedores-novos"
  }, {
    title: "Script de Vendas Pronto: O Roteiro Que Abre Porta e Marca o Próximo Passo",
    excerpt: "Roteiro de ligação comercial e script de prospecção: anatomia em 6 blocos, VSL, técnicas de perguntas, rapport e modelo em .txt para baixar.",
    category: "Processo Comercial",
    date: "25 Ago 2026",
    readTime: "12 min",
    image: postCoverScript,
    link: "/posts/script-de-vendas-pronto"
  }, {
    title: "Como Montar Uma Equipe de Vendas do Zero: O Passo a Passo Completo",
    excerpt: "10 passos para montar equipe comercial e estruturar time de vendas: quantos contratar, scorecard, playbook, comissão, rampagem de 90 dias e treino semanal.",
    category: "Estrutura Comercial",
    date: "22 Ago 2026",
    readTime: "17 min",
    image: postCoverMontarEquipe,
    link: "/posts/como-montar-equipe-de-vendas-do-zero"
  }, {
    title: "Lições da Copa 2026 Para Vendas: Não Perca o Gol na Hora do Pênalti",
    excerpt: "A 5 dias da final da Copa, 7 lições dos gramados que separam times comerciais campeões dos que perdem a meta no minuto 90. Treino contínuo com IA.",
    category: "Alta Performance",
    date: "14 Jul 2026",
    readTime: "10 min",
    image: postCoverCopa,
    link: "/posts/licoes-copa-2026-vendas"
  }, {
    title: "Funil de Vendas em 2026: Como Construir Pipeline Previsível Que Triplica Conversões",
    excerpt: "10 passos práticos: ICP, etapas binárias, conversão por estágio, forecast ponderado, lead scoring com IA, higienização e treino por etapa.",
    category: "Gestão Comercial",
    date: "01 Jun 2026",
    readTime: "18 min",
    image: postCoverFunil,
    link: "/posts/funil-vendas-2026"
  }, {
    title: "Negociação B2B em 2026: Como Conduzir Reuniões Que Fecham Grandes Contratos",
    excerpt: "13 passos práticos: BATNA, ZOPA, ancoragem, concessões, stakeholders, contratos, walk-away power e treino com IA.",
    category: "Negociação Avançada",
    date: "05 Mai 2026",
    readTime: "23 min",
    image: postCoverNegociacao,
    link: "/posts/negociacao-b2b-2026"
  }, {
    title: "Social Selling no LinkedIn em 2026: Como Gerar Pipeline Sem Cold Call",
    excerpt: "Guia completo em 13 passos: perfil, Sales Navigator, conteúdo, DM, eventos, métricas e integração ao CRM. Pipeline previsível sem cold call.",
    category: "Prospecção Digital",
    date: "22 Abr 2026",
    readTime: "24 min",
    image: postCoverSocialSelling,
    link: "/posts/social-selling-linkedin-2026"
  }, {
    title: "Pré-Vendas em 2026: Como Estruturar um Time SDR/BDR Que Gera Pipeline Previsível",
    excerpt: "Guia completo com 13 passos: ICP, cadência multicanal, qualificação BANT/CHAMP, handoff, métricas e IA aplicada.",
    category: "Estrutura Comercial",
    date: "18 Mar 2026",
    readTime: "22 min",
    image: postCoverPreVendas,
    link: "/posts/pre-vendas-sdr-bdr-2026"
  }, {
    title: "Como Vender Mais em 2026: 13 Passos Práticos Para Aplicar Hoje",
    excerpt: "O manual definitivo com checklist, cases reais e storytelling de quem já aplicou e vendeu. Comece hoje mesmo!",
    category: "Manual Prático",
    date: "15 Fev 2026",
    readTime: "20 min",
    image: postCoverComoVenderMais,
    link: "/posts/como-vender-mais-2026"
  }, {
    title: "13 Estratégias de Vendas Para Dominar Qualquer Negociação em 2026",
    excerpt: "SPIN, BANT, MEDDIC e mais 10 frameworks com tabela comparativa e níveis de maturidade.",
    category: "Técnicas de Vendas",
    date: "13 Jan 2026",
    readTime: "25 min",
    image: postCoverEstrategias,
    link: "/posts/13-estrategias-vendas-2026"
  }, {
    title: "Cold Calling em 2026: Está Morto ou Ainda Funciona?",
    excerpt: "Análise completa e atualizada sobre prospecção por telefone. Técnicas modernas e estratégias comprovadas.",
    category: "Prospecção",
    date: "10 Dez 2025",
    readTime: "15 min",
    image: postCoverColdcalling,
    link: "/posts/cold-calling-2026"
  }, {
    title: "Gamificação em Vendas: O Poder dos Pontos e Rankings",
    excerpt: "Descubra como sistemas de gamificação podem motivar sua equipe comercial.",
    category: "Gestão",
    date: "15 Nov 2025",
    readTime: "7 min",
    image: postCoverGamificacao,
    link: "/posts/gamificacao-vendas"
  }, {
    title: "Objeções de Preço: Como Transformar em Oportunidade",
    excerpt: "Aprenda a lidar com a objeção mais comum em vendas e converter prospects em clientes.",
    category: "Negociação",
    date: "20 Out 2025",
    readTime: "5 min",
    image: postCoverObjecoes,
    link: "/posts/objecoes-preco"
  }, {
    title: "Como a IA Está Revolucionando o Treinamento de Equipes Comerciais",
    excerpt: "Entenda como a inteligência artificial pode acelerar o desenvolvimento de vendedores e reduzir custos.",
    category: "Tecnologia",
    date: "18 Set 2025",
    readTime: "6 min",
    image: postCoverIA,
    link: "/posts/ia-treinamento-vendas"
  }, {
    title: "7 Técnicas de Fechamento que Todo Vendedor Precisa Dominar",
    excerpt: "Descubra as estratégias mais eficazes para fechar vendas complexas e aumentar sua taxa de conversão.",
    category: "Técnicas de Vendas",
    date: "20 Ago 2025",
    readTime: "8 min",
    image: postCoverTecnicas,
    link: "/posts/7-tecnicas-fechamento"
  }];
  const handlePlanClick = (plan: 'start' | 'expert' | 'max_pro') => {
    if (plan === 'start') {
      navigate(user ? '/dashboard' : '/auth?mode=signup&plan=start');
      return;
    }
    if (user) {
      navigate(`/upgrade?plan=${plan}`);
    } else {
      localStorage.setItem('pending_plan', plan);
      navigate(`/auth?plan=${plan}`);
    }
  };
  const handleCtaClick = () => {
    navigate(user ? '/dashboard' : '/auth?mode=signup');
  };
  const paraQuem = [{
    icon: Building2,
    titulo: "Dono de empresa",
    texto: "A venda ainda depende de você. Quer um time que venda sem precisar da sua presença em cada negociação."
  }, {
    icon: UserCog,
    titulo: "Gestor comercial",
    texto: "Precisa treinar o time toda semana, mas não tem tempo de sentar com cada vendedor para corrigir abordagem e objeção."
  }, {
    icon: Users,
    titulo: "Vendedor e SDR",
    texto: "Quer treinar objeção, abordagem e fechamento antes de errar com o cliente de verdade — e ver a própria evolução em nota."
  }];
  const faqs = [{
    pergunta: "Como funciona o treinamento de vendas com IA?",
    resposta: "Você cria personas de clientes com o perfil, o setor e as objeções que sua equipe enfrenta no dia a dia. O vendedor conversa com essa persona por texto, como numa negociação real, e no fim recebe uma nota e um feedback do que fez bem e do que precisa melhorar em abordagem, descoberta, contorno de objeção e fechamento."
  }, {
    pergunta: "Preciso de cartão de crédito para começar?",
    resposta: "Não. A conta gratuita permite criar sua persona e treinar para conhecer a plataforma. Você só escolhe um plano quando decidir liberar acesso para a equipe e acompanhar a evolução de cada vendedor."
  }, {
    pergunta: "Funciona para o meu setor?",
    resposta: "Sim. Como as personas são criadas a partir do seu produto, do seu ticket e das objeções do seu mercado, a plataforma se adapta a vendas B2B, B2C consultivas, serviços, seguros, indústria, tecnologia e varejo."
  }, {
    pergunta: "Quanto tempo por semana o vendedor precisa treinar?",
    resposta: "Dois a três treinos de 10 a 15 minutos por semana já mudam comportamento. O ganho vem da repetição frequente, não de um treinamento longo uma vez por ano."
  }, {
    pergunta: "Substitui o treinamento presencial e a consultoria?",
    resposta: "Não substitui: sustenta. O encontro presencial e a consultoria comercial definem processo e método; a plataforma mantém o time praticando entre um encontro e outro, que é onde a maioria dos programas de treinamento se perde."
  }, {
    pergunta: "O gestor consegue acompanhar a evolução do time?",
    resposta: "Sim. O painel do coordenador mostra quem treinou, com quais personas, a nota de cada simulação e a evolução ao longo do tempo, para a conversa de feedback ser baseada em dado e não em impressão."
  }];
  return <div className="min-h-screen bg-background">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Bianchim Business",
          url: "https://bianchimbusiness.com.br",
          logo: "https://bianchimbusiness.com.br/favicon.png",
          description: "Plataforma de treinamento de vendas com inteligência artificial e consultoria comercial para estruturar times de vendas.",
          telephone: "+55 16 98196-0018",
          areaServed: "BR",
          founder: { "@type": "Person", name: "Junior Bianchim" },
          sameAs: ["https://www.instagram.com/bianchimbusiness", "https://www.linkedin.com/company/bianchim-business"]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map(f => ({
            "@type": "Question",
            name: f.pergunta,
            acceptedAnswer: { "@type": "Answer", text: f.resposta }
          }))
        })}</script>
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center space-y-8 animate-fade-in">
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight">
            <span className="text-gradient-brand">
              Treinamento de Vendas
            </span>
            <br />
            <span className="text-foreground">com Inteligência Artificial</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Treinamento comercial contínuo: sua equipe treina em simulações reais com IA, recebe nota e evolui a cada conversa.
          </p>

          <HeroObjecaoChat />

          <p className="text-xs text-muted-foreground/80">
            Sem cartão de crédito. Teste a IA quebrando sua própria objeção agora mesmo.
          </p>

          <p className="text-sm text-muted-foreground flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-2">
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" />1000+ profissionais treinados</span>
            <span className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-primary" />100+ empresas atendidas</span>
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-primary" />99% de clientes satisfeitos</span>
            <span className="flex items-center gap-2"><Trophy className="w-4 h-4 text-primary" />20+ anos de experiência</span>
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mt-20 animate-slide-up">
          <Card className="p-6 border-primary/30 bg-card hover:border-primary/60 transition-all hover:shadow-glow">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-primary-foreground" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-foreground">Treinamento com IA</h3>
            <p className="text-muted-foreground">
              Simule conversas reais com personas de clientes e receba feedback instantâneo.
            </p>
          </Card>

          <Card className="p-6 border-accent/30 bg-card hover:border-accent/60 transition-all hover:shadow-accent-glow">
            <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center mb-4">
              <Trophy className="w-6 h-6 text-accent-foreground" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-foreground">Sistema de Pontos</h3>
            <p className="text-muted-foreground">
              Gamificação com melhorias e notas para motivar sua equipe.
            </p>
          </Card>

          <Card className="p-6 border-secondary/30 bg-card hover:border-secondary/60 transition-all">
            <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-secondary-foreground" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-foreground">Gestão Completa</h3>
            <p className="text-muted-foreground">
              Gerencie empresas, vendedores e personas de forma centralizada.
            </p>
          </Card>
        </div>
      </section>

      {/* Consultoria Section */}
      <section className="py-20 bg-muted/20" role="region" aria-label="Serviços de Consultoria">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient-brand">Estruturação Comercial</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Além da plataforma de IA, oferecemos soluções completas para transformar sua equipe comercial
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <Card className="p-6 border-border bg-card hover:border-primary/30 transition-all group text-center">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-foreground">Diagnóstico 360</h3>
              <p className="text-sm text-muted-foreground">Mapeamento completo da sua operação comercial</p>
            </Card>

            <Card className="p-6 border-border bg-card hover:border-primary/30 transition-all group text-center">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                <BookOpen className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-foreground">Treinamentos</h3>
              <p className="text-sm text-muted-foreground">SPIN Selling, BANT, Negociação e mais</p>
            </Card>

            <Card className="p-6 border-border bg-card hover:border-primary/30 transition-all group text-center">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                <Briefcase className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-foreground">Desenvolvimento de Equipe</h3>
              <p className="text-sm text-muted-foreground">Cultura comercial de alta performance</p>
            </Card>

            <Card className="p-6 border-border bg-card hover:border-primary/30 transition-all group text-center">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                <Mic className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-foreground">Palestras</h3>
              <p className="text-sm text-muted-foreground">Experiências de impacto para convenções</p>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow font-semibold" asChild>
              <Link to="/consultoria">
                Conhecer Serviços de Consultoria
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Objections Section */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 items-center mb-12">
            <div className="order-2 md:order-1 md:col-span-2">
              <h2 className="text-4xl font-bold mb-6">
                <span className="text-gradient-brand">
                  Por Que Escolher o Vendas Trainer?
                </span>
              </h2>
              <p className="text-foreground/80 text-lg leading-relaxed">
                Desenvolvido por <strong className="text-primary">Junior Bianchim</strong>, especialista em vendas com mais de 20 anos de experiência prática no mercado, atuando em grandes empresas como a TIM Brasil. Hoje é professor de Vendas, Marketing e Administração no SENAC e CEO da Bianchim Business, onde ajuda empresas e equipes comerciais a transformarem estratégia em resultado real.
              </p>
              <p className="text-foreground/90 text-lg mt-4 leading-relaxed">
                Todo o conhecimento foi testado em campo, com foco em <strong className="text-foreground">resultado</strong>, <strong className="text-foreground">performance comercial</strong> e <strong className="text-foreground">crescimento sustentável</strong>.
              </p>
              <a
                href="https://www.linkedin.com/in/junior-bianchim-017a64184/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                Conecte-se com Junior Bianchim
              </a>
            </div>
            <div className="order-1 md:order-2 md:col-span-1 h-full">
              <div className="relative h-full min-h-[320px] md:min-h-[480px] rounded-2xl overflow-hidden bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/15 border border-primary/20 shadow-2xl pt-4">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.25),transparent_60%)]"></div>
                <img
                  src={juniorBianchimNoBg}
                  alt="Junior Bianchim - CEO Bianchim Business"
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[105%] w-auto max-w-none object-contain object-bottom drop-shadow-2xl"
                />
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none"></div>
              </div>
            </div>

          </div>


          {/* CTA Before Objection Cards */}
          <div className="py-10 text-center">
            <h3 className="text-3xl font-bold mb-4 text-foreground">
              Pronto para transformar sua equipe de vendas?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Comece agora mesmo e veja resultados em poucos dias. Sem cartão de crédito.
            </p>
            <Button size="lg" onClick={handleCtaClick} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow font-semibold">
              Começar Agora - É Grátis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {objections.map((item, index) => <Card key={index} className="p-6 border-border bg-card hover:border-primary/30 transition-all">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-destructive" />
                  </div>
                  <div className="space-y-3">
                    <p className="font-semibold text-destructive italic">&quot;{item.objection}&quot;</p>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <p className="text-foreground/80">{item.response}</p>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Para quem é */}
      <section className="py-20" aria-label="Para quem é a plataforma">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient-brand">Para quem é</span>
            </h2>
            <p className="text-muted-foreground">
              Treinamento de equipe comercial para quem precisa de resultado consistente, não de mais um curso gravado.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {paraQuem.map((item, i) => <Card key={i} className="p-6 border-border bg-card hover:border-primary/30 transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">{item.titulo}</h3>
                <p className="text-muted-foreground text-sm">{item.texto}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-muted/20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            <span className="text-gradient-brand">
              Planos de Treinamento de Vendas
            </span>
          </h2>
          <p className="text-muted-foreground mb-8">Escolha o melhor para sua equipe</p>

          {/* Toggle Mensal / Anual */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center bg-muted rounded-full p-1">
              <button
                onClick={() => setPricingPeriod('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition ${pricingPeriod === 'monthly' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
              >
                Mensal
              </button>
              <button
                onClick={() => setPricingPeriod('annual')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition inline-flex items-center gap-2 ${pricingPeriod === 'annual' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
              >
                Anual
                <span className="text-[10px] font-bold bg-primary/20 text-primary px-2 py-0.5 rounded-full">-33%</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Plano Start */}
            <Card className="p-8 border-border bg-card">
              <h3 className="text-2xl font-bold mb-2 text-foreground">Start</h3>
              <p className="text-4xl font-bold text-primary mb-1">Grátis</p>
              <p className="text-sm text-muted-foreground mb-4">&nbsp;</p>
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">1 usuário</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">10 personas</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">10 feedbacks/mês</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full border-primary/50 hover:bg-primary/10" onClick={() => handlePlanClick('start')}>
                Começar Grátis
              </Button>
            </Card>

            {/* Plano Expert */}
            <Card className="p-8 border-primary bg-gradient-to-br from-primary/10 to-accent/10 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-primary text-xs font-bold text-primary-foreground">
                Popular
              </div>
              <h3 className="text-2xl font-bold mb-2 text-foreground">Expert</h3>
              <p className="text-4xl font-bold text-primary mb-1">
                {pricingPeriod === 'annual' ? 'R$ 29,90' : 'R$ 44,85'}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                /mês {pricingPeriod === 'annual' ? '(cobrado anualmente R$ 358,80)' : ''}
              </p>
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">Até 5 usuários</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">Personas ilimitadas</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">100 feedbacks/mês</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">Histórico completo</span>
                </li>
              </ul>
              <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow" onClick={() => handlePlanClick('expert')}>
                Escolher Expert
              </Button>
            </Card>

            {/* Plano Max Pro */}
            <Card className="p-8 border-accent/50 bg-card">
              <h3 className="text-2xl font-bold mb-2 text-foreground">Max Pro</h3>
              <p className="text-4xl font-bold text-accent mb-1">
                {pricingPeriod === 'annual' ? 'R$ 19,90' : 'R$ 29,85'}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                /usuário/mês {pricingPeriod === 'annual' ? '(anual R$ 238,80/usuário)' : ''}
              </p>
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className="text-sm text-muted-foreground">Usuários ilimitados</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className="text-sm text-muted-foreground">Personas ilimitadas</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className="text-sm text-muted-foreground">Feedbacks ilimitados</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className="text-sm text-muted-foreground">Suporte prioritário</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full border-accent/50 hover:bg-accent/10" onClick={() => handlePlanClick('max_pro')}>
                Escolher Max Pro
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel Section */}
      <TestimonialsCarousel testimonials={testimonials} />

      {/* FAQ Section */}
      <section className="py-20" aria-label="Perguntas frequentes">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient-brand">Perguntas frequentes</span>
            </h2>
            <p className="text-muted-foreground">Tudo que costumam perguntar antes de começar a treinar</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => <Card key={i} className="p-6 border-border bg-card">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-2">{faq.pergunta}</h3>
                    <p className="text-muted-foreground leading-relaxed">{faq.resposta}</p>
                  </div>
                </div>
              </Card>)}
          </div>
          <p className="text-center text-muted-foreground mt-10">
            Precisa estruturar o processo antes de treinar? Conheça a{" "}
            <Link to="/consultoria" className="text-primary hover:underline font-medium">consultoria comercial</Link>.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <Card className="p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-gradient-brand">
                  Transforme Sua Equipe Hoje
                </span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Junte-se a centenas de empresas que já estão acelerando suas vendas com treinamento inteligente.
                <strong className="text-foreground"> Comece grátis, sem cartão de crédito.</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" onClick={handleCtaClick} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold">
                  Criar Conta Grátis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                  <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                    Agendar Demonstração
                  </a>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                ✓ Setup em 5 minutos &nbsp; ✓ Suporte em português &nbsp; ✓ Cancele quando quiser
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient-brand">
                Blog Bianchim Business
              </span>
            </h2>
            <p className="text-muted-foreground">Dicas, estratégias e novidades do mundo das vendas</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* First 2 posts - vertical cards side by side */}
            {blogPosts.slice(0, 2).map((post, index) => <Link key={index} to={post.link!} className="block">
                <Card className="overflow-hidden border-border bg-card hover:border-primary/30 transition-all cursor-pointer group h-full flex flex-col">
                  <div className="w-full h-48 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                        {post.category}
                      </span>
                      <span className="text-xs text-muted-foreground">{post.date}</span>
                      <span className="text-xs text-muted-foreground">• {post.readTime}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors">{post.title}</h3>
                    <p className="text-muted-foreground text-sm flex-1">{post.excerpt}</p>
                    <div className="mt-4 flex items-center text-primary font-medium text-sm">
                      Ler artigo
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Card>
              </Link>)}

            {/* Third column - 3 horizontal compact posts */}
            <div className="space-y-4">
              {blogPosts.slice(2, 5).map((post, index) => {
              const content = <Card className="p-4 border-border bg-card hover:border-primary/30 transition-all cursor-pointer group">
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-medium text-accent bg-accent/10 px-2 py-1 rounded-full">
                            {post.category}
                          </span>
                          <span className="text-xs text-muted-foreground">{post.date}</span>
                          <span className="text-xs text-muted-foreground">• {post.readTime}</span>
                        </div>
                        <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">{post.title}</h4>
                        <p className="text-muted-foreground text-xs mt-1 line-clamp-1">{post.excerpt}</p>
                      </div>
                    </div>
                  </Card>;
              return post.link ? <Link key={index} to={post.link} className="block">
                    {content}
                  </Link> : <div key={index} className="opacity-60">{content}</div>;
            })}
            </div>
          </div>

          <div className="text-center mt-10">
            <Button variant="outline" className="border-primary/50 hover:bg-primary/10" asChild>
              <Link to="/blog">
                Ver Todos os Artigos
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default Landing;